import React, { useEffect, useState } from "react";
import axios from "axios";
import FrontendLayout from "../../../components/frontend/FrontendLayout";
import UserCommon from "../../../components/frontend/UserCommon";
import UserTabs from "../../../components/frontend/UserTabs";
import { formatShortPrice } from "../../../utils/formatNumber";
import { formatNumberIndian } from "../../../utils/formatNumber";
import { handleApiError } from "../../../utils/apiHelpers";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import NoResults from "../../../components/admin/common/NoResults";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Land {
  wl_id: number;
  wl_userid: number;
  wl_landid: number;
  ip?: string;
  wl_createdat: string;
  ln_id: number;
  land_id: number;
  ln_title: string;
  ln_price: number;
  ln_total_area_sqft: number;
  ln_total_sqft_area: string;
  ln_post_date: string;
  ln_plottype: string;
  ln_locality: string;
  ln_address: string;
  ln_image?: string;
  image_files?: string[];
  ln_status?: string;
  ln_seo_url: string;
}

const Activities: React.FC = () => {
  const [favourites, setFavourites] = useState<Land[]>([]);
  const [viewed, setViewed] = useState<Land[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Favourites");
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("frontendUserToken");
  const userType = localStorage.getItem("frontendUserType");
  const userId = localStorage.getItem("frontendUserId");

  const profile = useUserProfile(token);

  const fetchFavourites = async () => {
    try {
      if (!userId) return;
      const res = await axios.get(`${API_BASE}/api/wishlist/list/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setFavourites(res.data.wishlist || []);
      } else {
        console.error("Failed to fetch favourites:", res.data.message);
      }
    } catch (err) {
      handleApiError(err, navigate);
    }
  };

  const fetchViewed = async () => {
    try {
      if (!userId) return;
      const res = await axios.get(
        `${API_BASE}/api/landdata/recentviews/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        // Map recentViews to match the Land interface
        const formattedViews = res.data.recentViews.map((view: any) => ({
          land_id: view.land_id,
          user_id: view.user_id,
          last_viewed: view.last_viewed,
          ln_title: view.ln_title,
          ln_price: view.ln_price,
          ln_total_area_sqft: view.ln_total_area_sqft,
          ln_total_sqft_area: view.ln_total_sqft_area.toString(), // Convert to string to match interface
          ln_plottype: view.ln_plottype,
          ln_seo_url: view.ln_seo_url,
          ln_locality: view.ln_locality,
          ln_address: view.ln_address,
          ln_post_date: view.ln_post_date,  
          image_files: view.image_files,
          video_files: view.video_files,
          cert_files: view.cert_files,
          drone_files: view.drone_files,
        }));
        setViewed(formattedViews || []);
      } else {
        console.error("Failed to fetch viewed lands:", res.data.message);
      }
    } catch (err) {
      handleApiError(err, navigate);
    }
  };

  useEffect(() => {
    if (userType !== "3") {
      navigate("/403");
      return;
    }
    fetchFavourites();
    fetchViewed();
  }, [userType, navigate, userId]);

  useEffect(() => {
    if (location.state && (location.state as any).message) {
      toast.success((location.state as any).message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const filteredFavourites = favourites.filter(
    (land) =>
      (land.ln_locality?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_address?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_plottype?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const filteredViewed = viewed.filter(
    (land) =>
      (land.ln_locality?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_address?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_plottype?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <div className="row">
                  <div className="col-lg-8 pb-2 d-flex align-items-center">
                    <a
                      href="#"
                      className={`btn ${
                        activeTab === "Favourites"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange("Favourites");
                      }}
                    >
                      Favourites
                    </a>
                    <a
                      href="#"
                      className={`btn ${
                        activeTab === "Viewed"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange("Viewed");
                      }}
                    >
                      Viewed
                    </a>
                  </div>
                  <div className="col-lg-4">
                    <div className="input-group form">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by locality, address, or Plot type..."
                        aria-label="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <button
                        className="btn btn-primary btn-search"
                        type="button"
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                  </div>
                  <div className="col-lg-12 pb-5">
                    <hr />
                  </div>

                  <div className="col-lg-12">
                    {activeTab === "Favourites" ? (
                      <>
                        <h3>Favourites</h3>
                        {filteredFavourites.length > 0 ? (
                          <div className="row">
                            {filteredFavourites.map((land) => (
                              <div className="col-lg-3 pb-5" key={land.wl_id}>
                                <div className="box-location hover-img">
                                  <div className="plot-location">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    <div className="plot-location-text ms-2 text-16">
                                      {land.ln_locality},<br />{" "}
                                      {land.ln_address}
                                    </div>
                                  </div>
                                  <div
                                    className={`plot-details3 sec${
                                      (land.wl_id % 5) + 1
                                    }`}
                                  >
                                    <div className="plot-details-row text-2">
                                      {land.ln_total_sqft_area} sqft
                                      <br />
                                      Plot Area
                                    </div>
                                    <div className="plot-details-row text-2">
                                      {land.ln_plottype}
                                    </div>
                                    <div className="plot-details-row text-2 no-border">
                                      {new Date(
                                        land.ln_post_date
                                      ).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                      })}
                                      <br />
                                      Posted on
                                    </div>
                                  </div>
                                  <div className="image-wrap land-img">
                                    <Link to={`/${land.ln_seo_url}`}>
                                      <img
                                        className="ls-is-cached lazyloaded"
                                        src={
                                          land.image_files &&
                                          land.image_files.length > 0
                                            ? `${API_BASE}/uploads/lands/${land.image_files[0]}`
                                            : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                        }
                                        alt="Plot"
                                      />
                                    </Link>
                                  </div>
                                  <div className="content">
                                    <h4 className="text_white">
                                      <i className="bi bi-currency-rupee me-1"></i>
                                      {formatShortPrice(land.ln_price)} <br />
                                      <small className="text-1">
                                        <i className="bi bi-currency-rupee me-1"></i>
                                        {formatNumberIndian(
                                          land.ln_total_area_sqft
                                        )}{" "}
                                        per sqft
                                      </small>
                                    </h4>
                                  </div>
                                  <Link
                                    to={`/${land.ln_seo_url}`}
                                    className="tf-btn style-border view-more text-3"
                                  >
                                    View Details
                                    <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <NoResults description="No favourite lands found. Try adjusting your search." />
                        )}
                      </>
                    ) : (
                      <>
                        <h3>Viewed</h3>
                        {filteredViewed.length > 0 ? (
                          <div className="row">
                            {filteredViewed.map((land) => (
                              <div className="col-lg-3 pb-5" key={land.land_id}>
                                <div className="box-location hover-img">
                                  <div className="plot-location">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    <div className="plot-location-text ms-2 text-16">
                                      {land.ln_locality},<br />{" "}
                                      {land.ln_address}
                                    </div>
                                  </div>
                                  <div
                                    className={`plot-details3 sec${
                                      (land.land_id % 5) + 1
                                    }`}
                                  >
                                    <div className="plot-details-row text-2">
                                      {land.ln_total_sqft_area} sqft
                                      <br />
                                      Plot Area
                                    </div>
                                    <div className="plot-details-row text-2">
                                      {land.ln_plottype}
                                    </div>
                                    <div className="plot-details-row text-2 no-border">
                                      {new Date(
                                        land.ln_post_date
                                      ).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                      })}
                                      <br />
                                      Posted on
                                    </div>
                                  </div>
                                  <div className="image-wrap land-img">
                                    <Link to={`/${land.ln_seo_url}`}>
                                      <img
                                        className="ls-is-cached lazyloaded"
                                        src={
                                          land.image_files &&
                                          land.image_files.length > 0
                                            ? `${API_BASE}/uploads/lands/${land.image_files[0]}`
                                            : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                        }
                                        alt="Plot"
                                      />
                                    </Link>
                                  </div>
                                  <div className="content">
                                    <h4 className="text_white">
                                      <i className="bi bi-currency-rupee me-1"></i>
                                      {formatShortPrice(land.ln_price)} <br />
                                      <small className="text-1">
                                        <i className="bi bi-currency-rupee me-1"></i>
                                        {formatNumberIndian(
                                          land.ln_total_area_sqft
                                        )}{" "}
                                        per sqft
                                      </small>
                                    </h4>
                                  </div>
                                  <Link
                                    to={`/${land.ln_seo_url}`}
                                    className="tf-btn style-border view-more text-3"
                                  >
                                    View Details
                                    <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <NoResults description="No viewed lands available at the moment." />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default Activities;
